import { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "motion/react";
import {
  ShoppingCart,
  Clock,
  Truck,
  Star,
  Users,
  Target,
  Lightbulb,
  Layers,
  Palette,
  TestTube,
  CheckCircle,
  Zap,
  Heart,
  TrendingUp,
  HelpCircle,
  MessageSquare,
  FileText,
  BarChart3,
  AlertCircle,
} from "lucide-react";
import "./index.css";

// Scroll reveal component
function ScrollReveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// Parallax section component
function ParallaxSection({ children, speed = 0.5 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [100 * speed, -100 * speed]);

  return (
    <div ref={ref} className="relative">
      <motion.div style={{ y }}>{children}</motion.div>
    </div>
  );
}

export default function GreenBasketPage() {
  return (
    <div className="bg-[#0d0d0d] text-white min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1748342319942-223b99937d4e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMHZlZ2V0YWJsZXMlMjBtYXJrZXR8ZW58MXx8fHwxNzY0Mzg0Mzk0fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            alt="Fresh vegetables"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d] via-transparent to-[#0d0d0d]" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="inline-block mb-6 px-6 py-2 bg-[#fd5b38]/10 item-border border-[#fd5b38]/30 rounded-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <span className="text-[#fd5b38]">UX Case Study 2025</span>
            </motion.div>

            <motion.h1
              className="mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <span className="block text-5xl md:text-7xl lg:text-8xl mb-4">
                Green Basket
              </span>
              <span className="block text-3xl md:text-4xl lg:text-5xl text-gray-400">
                E-Commerce Grocery Application
              </span>
            </motion.h1>

            <motion.p
              className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              A one-stop hyperlocal quick commerce solution for ordering
              groceries online with subscription capabilities and rapid
              delivery.
            </motion.p>

            <motion.div
              className="flex gap-4 justify-center flex-wrap"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
            >
              <div className="flex items-center gap-2 text-gray-400">
                <Clock className="w-5 h-5 text-[#fd5b38]" />
                <span>10-15 min delivery</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <ShoppingCart className="w-5 h-5 text-[#fd5b38]" />
                <span>Fresh groceries</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Zap className="w-5 h-5 text-[#fd5b38]" />
                <span>Subscription based</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="text-[#fd5b38]"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M12 5v14M19 12l-7 7-7-7"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
        </motion.div>
      </section>
      {/* Introduction Section */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Introduction</h2>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <ScrollReveal delay={0.2}>
              <div className="space-y-6 text-gray-300 text-lg">
                <p>
                  Grocery delivery apps have gained enormous popularity after
                  the Coronavirus pandemic. According to Bain & Company's
                  research, before the pandemic, only 3%-4% of customers used
                  online apps. During the pandemic, this number surged to 10-15%
                  — a ~5x bump.
                </p>
                <p>
                  Green Basket is designed to bridge the gaps in current
                  customer experience by providing a seamless,
                  subscription-based grocery ordering platform with ultra-fast
                  delivery times.
                </p>
                <div className="grid grid-cols-3 gap-4 pt-6">
                  <div className="text-center">
                    <div className="text-4xl text-[#fd5b38] mb-2">5x</div>
                    <div className="text-sm text-gray-400">Growth in users</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl text-[#fd5b38] mb-2">10min</div>
                    <div className="text-sm text-gray-400">Avg. delivery</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl text-[#fd5b38] mb-2">24/7</div>
                    <div className="text-sm text-gray-400">Availability</div>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ParallaxSection speed={0.3}>
              <ScrollReveal delay={0.4}>
                <div className="relative">
                  <div className="absolute inset-0 bg-[#fd5b38]/20 blur-3xl rounded-full" />
                  <img
                    src="https://images.unsplash.com/photo-1703007739216-e2501be0df89?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxncm9jZXJ5JTIwc2hvcHBpbmclMjBtb2JpbGV8ZW58MXx8fHwxNzY0NDE2NjM3fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                    alt="Mobile grocery shopping"
                    className="relative rounded-2xl shadow-2xl"
                  />
                </div>
              </ScrollReveal>
            </ParallaxSection>
          </div>
        </div>
      </section>
      {/* Problem & Solution */}
      <section className="py-32 px-6 bg-gradient-to-b from-transparent via-[#fd5b38]/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Problem & Solution</h2>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-16">
            <ScrollReveal delay={0.2}>
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-2xl mb-3">The Problem</h3>
                    <p className="text-gray-300">
                      Monthly online grocery spending has more than doubled
                      post-pandemic. However, existing platforms have
                      significant gaps in customer experience including unclear
                      delivery times, lack of fresh daily deliveries, and no
                      integrated subscription options.
                    </p>
                  </div>
                </div>

                <div className="space-y-4 pl-16">
                  {[
                    "Waste of time in traditional grocery shopping",
                    "Imprecise estimated delivery times",
                    "No option for fresh daily deliveries",
                    "Limited deals and offers at local stores",
                    "Fragmented experience across multiple apps",
                  ].map((point, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-start gap-3 text-gray-400"
                    >
                      <div className="w-2 h-2 rounded-full bg-red-500 mt-2 flex-shrink-0" />
                      <span>{point}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#fd5b38]/10 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-6 h-6 text-[#fd5b38]" />
                  </div>
                  <div>
                    <h3 className="text-2xl mb-3">The Solution</h3>
                    <p className="text-gray-300">
                      Building a mobile application that manages the complete
                      grocery ordering flow. Green Basket provides an integrated
                      solution for both one-time purchases and subscriptions
                      with fresh groceries, real-time tracking, and tailored
                      product suggestions.
                    </p>
                  </div>
                </div>

                <div className="space-y-4 pl-16">
                  {[
                    "10-15 minute quick delivery",
                    "Daily subscription for fresh products",
                    "Real-time order tracking",
                    "Personalized deals and offers",
                    "Integrated one-stop platform",
                    "Guest checkout for easy browsing",
                  ].map((point, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-start gap-3 text-gray-300"
                    >
                      <CheckCircle className="w-5 h-5 text-[#fd5b38] flex-shrink-0" />
                      <span>{point}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      {/* Design Process */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Design Process</h2>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {[
              {
                icon: Users,
                title: "Discover",
                subtitle: "User Interviews, Competitive Analysis",
              },
              {
                icon: Target,
                title: "Define",
                subtitle: "User Persona, Empathy Map",
              },
              {
                icon: Lightbulb,
                title: "Ideate",
                subtitle: "Information Architecture",
              },
              {
                icon: Palette,
                title: "Design",
                subtitle: "Wireframes, Style Guide, Visual Design",
              },
              {
                icon: TestTube,
                title: "Test",
                subtitle: "Usability Testing",
              },
            ].map((step, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div
                  className="relative p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/0 item-border border-white/10 hover:border-[#fd5b38]/50 transition-all group"
                  whileHover={{ scale: 1.05, y: -5 }}
                >
                  <div className="absolute inset-0 bg-[#fd5b38]/0 group-hover:bg-[#fd5b38]/5 rounded-2xl transition-colors" />
                  <step.icon className="w-8 h-8 text-[#fd5b38] mb-4" />
                  <div className="text-xl mb-2">{step.title}</div>
                  <div className="text-sm text-gray-400">{step.subtitle}</div>
                  <div className="absolute top-6 right-6 text-6xl opacity-5 group-hover:opacity-10 transition-opacity">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      {/* User Research */}
      <section className="py-32 px-6 bg-gradient-to-b from-[#fd5b38]/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">User Research</h2>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <ScrollReveal delay={0.2}>
              <div className="p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/0 item-border border-white/10">
                <h3 className="text-2xl mb-6 flex items-center gap-3">
                  <Users className="w-6 h-6 text-[#fd5b38]" />
                  User Interviews
                </h3>
                <p className="text-gray-300 mb-6">
                  Conducted 10 user interviews with users aged 20-40 years to
                  gain deeper insights into their grocery shopping pain points
                  and preferences.
                </p>
                <div className="space-y-4">
                  {[
                    "Delivery time expectations unclear",
                    "Waste of time in traditional shopping",
                    "Need for daily fresh deliveries",
                    "Desire for better deals and offers",
                  ].map((insight, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <Star className="w-5 h-5 text-[#fd5b38] flex-shrink-0 mt-1" />
                      <span className="text-gray-300">{insight}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <div className="p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/0 item-border border-white/10">
                <h3 className="text-2xl mb-6 flex items-center gap-3">
                  <TrendingUp className="w-6 h-6 text-[#fd5b38]" />
                  Competitive Analysis
                </h3>
                <p className="text-gray-300 mb-6">
                  Analyzed BigBasket, Instamart, Dunzo Daily, Flipkart, and
                  Blinkit to identify market gaps and opportunities.
                </p>
                <div className="space-y-4">
                  {[
                    "Most apps require registration before browsing",
                    "Limited integrated subscription options",
                    "Quick delivery becoming standard",
                    "Gift options rarely available",
                  ].map((insight, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <Star className="w-5 h-5 text-[#fd5b38] flex-shrink-0 mt-1" />
                      <span className="text-gray-300">{insight}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>

          <ParallaxSection speed={0.2}>
            <ScrollReveal delay={0.6}>
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1759752394397-3c745feb24e0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx1c2VyJTIwcmVzZWFyY2glMjBub3RlYm9va3xlbnwxfHx8fDE3NjQ0MTY2Mzh8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="User research"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent" />
              </div>
            </ScrollReveal>
          </ParallaxSection>
        </div>
      </section>
      {/* User Survey & Key Insights */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">
                User Survey & Key Insights
              </h2>
            </div>
          </ScrollReveal>

          {/* Survey Questions */}
          <ScrollReveal delay={0.2}>
            <div className="mb-20">
              <div className="flex items-center gap-3 mb-8">
                <MessageSquare className="w-8 h-8 text-[#fd5b38]" />
                <h3 className="text-3xl">Survey Questions</h3>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  "When would you expect the delivery of the ordered items?",
                  "How frequently do you go grocery shopping?",
                  "What kind of issues do you face while ordering grocery items?",
                  "Do you get attracted by offers/discounts and end up buying things that you did not intend to buy?",
                  "Would you like fresh groceries delivered daily to your doorstep?",
                  "Do you usually make a list before going grocery shopping?",
                  "What platform do you prefer ordering groceries on and why?",
                  "Do you buy groceries when you are running low on items?",
                  "Would you like the real-time tracking of the orders?",
                ].map((question, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="group"
                  >
                    <div className="p-6 rounded-xl bg-gradient-to-br from-white/5 to-white/0 item-border border-white/10 hover:border-[#fd5b38]/30 transition-all h-full">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#fd5b38]/10 flex items-center justify-center group-hover:bg-[#fd5b38]/20 transition-colors">
                          <HelpCircle className="w-5 h-5 text-[#fd5b38]" />
                        </div>
                        <p className="text-gray-300 group-hover:text-white transition-colors">
                          {question}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </ScrollReveal>

          {/* Key Insights Derived from Interviews */}
          <ScrollReveal delay={0.4}>
            <div>
              <div className="flex items-center gap-3 mb-8">
                <FileText className="w-8 h-8 text-[#fd5b38]" />
                <h3 className="text-3xl">
                  Key Insights Derived from Interviews
                </h3>
              </div>

              <div className="space-y-6">
                {[
                  "Users don't get many deals and offers from local Kirana stores.",
                  "The most common problem users face is a waste of time while grocery shopping. Apps can resolve them and schedule their delivery. Also, now they will not waste their time finding the location of items.",
                  "The estimated delivery time is mostly not clear and precise.",
                  "Usually, people order 2-3 times a week or when they are running low on items.",
                  "Users using apps can also know about the cart value before checkout.",
                  "They can also save the items in their wishlist to shop for later.",
                  "Users don't prefer to go to the grocery shop every morning to get fresh products.",
                ].map((insight, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: i * 0.08,
                      type: "spring",
                      stiffness: 100,
                    }}
                    className="group"
                  >
                    <div className="p-6 rounded-xl bg-gradient-to-r from-[#fd5b38]/10 via-[#fd5b38]/5 to-transparent border-l-4 border-[#fd5b38] hover:from-[#fd5b38]/15 hover:via-[#fd5b38]/8 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#fd5b38] mt-2.5" />
                        <p className="text-gray-300 text-lg group-hover:text-white transition-colors">
                          {insight}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* Competitive Analysis */}
      <section className="py-32 px-6 bg-gradient-to-b from-[#fd5b38]/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Competitive Analysis</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="mb-12">
              <p className="text-xl text-gray-300">
                Understanding the factors of the competitors' apps would help to
                strategically design solutions to make a superior product as
                well as experience.
              </p>
            </div>
          </ScrollReveal>

          {/* Comparison Table */}
          <ScrollReveal delay={0.3}>
            <div className="mb-16 overflow-x-auto">
              <div className="min-w-[800px] rounded-2xl item-border border-white/10 overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-6 bg-gradient-to-r from-green-600 to-emerald-600">
                  <div className="col-span-1"></div>
                  {[
                    "Big Basket",
                    "Instamart",
                    "Dunzo Daily",
                    "Flipkart",
                    "Blinkit",
                  ].map((competitor, i) => (
                    <div
                      key={i}
                      className="p-4 text-center border-l border-white/20"
                    >
                      <div className="text-white">{competitor}</div>
                    </div>
                  ))}
                </div>

                {/* Table Rows */}
                {[
                  {
                    feature: "Guest Checkout",
                    availability: [false, false, false, false, false],
                  },
                  {
                    feature: "Subscription Option",
                    availability: [false, false, false, false, false],
                  },
                  {
                    feature: "Realtime Order Tracking",
                    availability: [false, true, true, false, true],
                  },
                  {
                    feature: "Quick Delivery Options",
                    availability: [false, true, true, false, true],
                  },
                  {
                    feature: "Refunds",
                    availability: [true, true, true, true, true],
                  },
                  {
                    feature: "Chat Support",
                    availability: [true, true, true, true, true],
                  },
                  {
                    feature: "Search In Categories",
                    availability: [true, true, true, true, true],
                  },
                  {
                    feature: "Gift Option",
                    availability: [true, false, false, false, true],
                  },
                ].map((row, rowIndex) => (
                  <motion.div
                    key={rowIndex}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: rowIndex * 0.05 }}
                    className={`grid grid-cols-6 ${
                      rowIndex % 2 === 0 ? "bg-white/5" : "bg-white/0"
                    } hover:bg-white/10 transition-colors`}
                  >
                    <div className="col-span-1 p-4 border-t border-white/10">
                      <div className="text-gray-300">{row.feature}</div>
                    </div>
                    {row.availability.map((available, colIndex) => (
                      <div
                        key={colIndex}
                        className="p-4 border-l border-t border-white/10 flex items-center justify-center"
                      >
                        <motion.div
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          viewport={{ once: true }}
                          transition={{
                            delay: rowIndex * 0.05 + colIndex * 0.02,
                            type: "spring",
                            stiffness: 200,
                          }}
                          className={`w-6 h-6 rounded-full ${
                            available ? "bg-green-500" : "bg-red-500"
                          }`}
                        />
                      </div>
                    ))}
                  </motion.div>
                ))}
              </div>
            </div>
          </ScrollReveal>

          {/* Key Insights */}
          <ScrollReveal delay={0.5}>
            <div>
              <div className="flex items-center gap-3 mb-8">
                <BarChart3 className="w-8 h-8 text-[#fd5b38]" />
                <h3 className="text-3xl">
                  Key Insights Derived from Competitive Analysis
                </h3>
              </div>

              <div className="space-y-6">
                {[
                  "Most of the apps ask the users to register even before they can explore the app. Sometimes, users just want to browse, see if the product is available, and compare prices. It is always a good experience to have an option for the user to explore the app even without registering.",
                  "Most of the apps have a completely different app for subscription, or don't have that option at all. Users would like to have fresh groceries delivered every morning to have a hassle-free life by not visiting supermarkets every day.",
                  "A few of the apps have recently started quick deliveries at the doorstep. It has become a necessity as well as a convenient option for users.",
                  "Sometimes, users might forget some important events in their personal lives and would like to send a bar of chocolate, cake, or any other gift to their friends or family. Having a gift option makes this whole process a lot easier.",
                ].map((insight, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: i * 0.08,
                      type: "spring",
                      stiffness: 100,
                    }}
                    className="group"
                  >
                    <div className="p-6 rounded-xl bg-gradient-to-r from-[#fd5b38]/10 via-[#fd5b38]/5 to-transparent border-l-4 border-[#fd5b38] hover:from-[#fd5b38]/15 hover:via-[#fd5b38]/8 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#fd5b38] mt-2.5" />
                        <p className="text-gray-300 text-lg group-hover:text-white transition-colors">
                          {insight}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* User Persona */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">User Persona</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="mb-12">
              <p className="text-xl text-gray-300">
                By understanding the motivations and frustrations of target
                users, it will be possible to design a product that will satisfy
                their needs. A hypothetical persona was crafted, using the
                gathered insights. The persona helped to prioritize the type of
                users and gave a clear idea to execute combined solutions to
                their problems. Building a persona helped in solving the process
                and opened new design opportunities.
              </p>
            </div>
          </ScrollReveal>

          {/* Persona Card */}
          <ScrollReveal delay={0.3}>
            <div className="p-8 md:p-12 rounded-2xl bg-gradient-to-br from-[#fd5b38]/10 via-white/5 to-white/0 item-border border-[#fd5b38]/20 mb-16">
              {/* Profile Header */}
              <div className="flex flex-col md:flex-row gap-8 mb-12 pb-8 border-b border-white/10">
                <div className="flex flex-col items-center text-center md:items-start md:text-left">
                  <div className="w-40 h-40 rounded-full bg-gradient-to-br from-[#fd5b38] to-orange-600 mb-4 flex items-center justify-center text-6xl">
                    👨‍💻
                  </div>
                  <h3 className="text-4xl mb-3">Animesh</h3>
                  <div className="space-y-2 text-gray-300">
                    <p className="flex items-center gap-2 justify-center md:justify-start">
                      <span className="text-[#fd5b38]">Occupation:</span>
                      <span>IT Employee</span>
                    </p>
                    <p className="flex items-center gap-2 justify-center md:justify-start">
                      <span className="text-[#fd5b38]">Location:</span>
                      <span>Chennai, Tamil Nadu</span>
                    </p>
                  </div>
                </div>

                <div className="flex-1">
                  <h4 className="text-2xl mb-4 text-[#fd5b38]">Bio :</h4>
                  <p className="text-gray-300 text-lg">
                    Animesh works in a startup as a software engineer. He lives
                    in Chennai, Tamil Nadu. His age is 23 years. He is quite
                    occupied with working from the office and doesn't get much
                    time to cook his own meals or do any shopping. He relies on
                    apps for almost everything.
                  </p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Motivations */}
                <div className="p-6 rounded-xl bg-gradient-to-br from-green-500/10 to-transparent item-border border-green-500/20">
                  <h4 className="text-2xl mb-4 text-[#fd5b38] flex items-center gap-2">
                    <Heart className="w-6 h-6" />
                    Motivations :
                  </h4>
                  <div className="space-y-3 text-gray-300">
                    <p className="flex items-start gap-2">
                      <span className="text-green-500 flex-shrink-0 mt-1">
                        →
                      </span>
                      <span>Quick grocery delivery</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-green-500 flex-shrink-0 mt-1">
                        →
                      </span>
                      <span>Subscription based delivery</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-green-500 flex-shrink-0 mt-1">
                        →
                      </span>
                      <span>Reduce time for grocery shopping.</span>
                    </p>
                  </div>
                </div>

                {/* Technology */}
                <div className="p-6 rounded-xl bg-gradient-to-br from-blue-500/10 to-transparent item-border border-blue-500/20">
                  <h4 className="text-2xl mb-4 text-[#fd5b38] flex items-center gap-2">
                    <Zap className="w-6 h-6" />
                    Technology :
                  </h4>
                  <div className="space-y-3 text-gray-300">
                    <p className="flex items-start gap-2">
                      <span className="text-blue-500 flex-shrink-0 mt-1">
                        →
                      </span>
                      <span>Smartphones</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-blue-500 flex-shrink-0 mt-1">
                        →
                      </span>
                      <span>Shopping apps</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-blue-500 flex-shrink-0 mt-1">
                        →
                      </span>
                      <span>Social Media.</span>
                    </p>
                  </div>
                </div>

                {/* Goals & Interest */}
                <div className="p-6 rounded-xl bg-gradient-to-br from-purple-500/10 to-transparent item-border border-purple-500/20">
                  <h4 className="text-2xl mb-4 text-[#fd5b38] flex items-center gap-2">
                    <Target className="w-6 h-6" />
                    Goals & Interest :
                  </h4>
                  <div className="space-y-3 text-gray-300">
                    <p className="flex items-start gap-2">
                      <span className="text-purple-500 flex-shrink-0 mt-1">
                        •
                      </span>
                      <span>Wants quick delivery of groceries.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-purple-500 flex-shrink-0 mt-1">
                        •
                      </span>
                      <span>Wants to have fresh milk every morning.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-purple-500 flex-shrink-0 mt-1">
                        •
                      </span>
                      <span>
                        Wants to reduce the burden of grocery shopping.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-purple-500 flex-shrink-0 mt-1">
                        •
                      </span>
                      <span>
                        Wants to have great offers and deals on grocery
                        shopping.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-purple-500 flex-shrink-0 mt-1">
                        •
                      </span>
                      <span>
                        Wants to easily place the order with minimum steps.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-purple-500 flex-shrink-0 mt-1">
                        •
                      </span>
                      <span>
                        Wants to schedule the delivery before his cook comes.
                      </span>
                    </p>
                  </div>
                </div>

                {/* Pain Points & Concerns */}
                <div className="p-6 rounded-xl bg-gradient-to-br from-red-500/10 to-transparent item-border border-red-500/20">
                  <h4 className="text-2xl mb-4 text-[#fd5b38] flex items-center gap-2">
                    <AlertCircle className="w-6 h-6" />
                    Pain Points & Concerns :
                  </h4>
                  <div className="space-y-3 text-gray-300">
                    <p className="flex items-start gap-2">
                      <span className="text-red-500 flex-shrink-0 mt-1">✗</span>
                      <span>
                        Very few applications provide options for quick
                        delivery.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-red-500 flex-shrink-0 mt-1">✗</span>
                      <span>
                        No option to get fresh milk delivered every morning.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-red-500 flex-shrink-0 mt-1">✗</span>
                      <span>
                        No good deals and offers available at Kirana and Kirana
                        shops.
                      </span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-red-500 flex-shrink-0 mt-1">✗</span>
                      <span>Feels grocery shopping is a boring chore.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-red-500 flex-shrink-0 mt-1">✗</span>
                      <span>
                        Doesn't have much time to go to a shop to buy groceries.
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Scenario */}
              <div className="mb-8 p-6 rounded-xl bg-gradient-to-r from-[#fd5b38]/10 to-transparent border-l-4 border-[#fd5b38]">
                <h4 className="text-2xl mb-4 text-[#fd5b38]">Scenario :</h4>
                <p className="text-gray-300 text-lg">
                  Looking for options for quick and hassle-free grocery delivery
                  at the doorstep. Also wants to have fresh milk and vegetables
                  every morning. Wants to do all these things from one app
                  itself to save time.
                </p>
              </div>

              {/* Quote */}
              <div className="p-8 rounded-xl bg-gradient-to-br from-white/5 to-transparent item-border border-white/10 relative">
                <div className="absolute top-4 left-4 text-6xl text-[#fd5b38] opacity-20">
                  "
                </div>
                <div className="absolute bottom-4 right-4 text-6xl text-[#fd5b38] opacity-20">
                  "
                </div>
                <h4 className="text-xl mb-4 text-[#fd5b38]">Quote:</h4>
                <p className="text-gray-300 text-lg italic relative z-10">
                  "I want the groceries to be delivered as soon as possible. I
                  want to get fresh milk every morning. I am a bachelor and I
                  want my groceries to be delivered before my cook comes home. I
                  should be able to have some option that will reduce the burden
                  to go for grocery shopping and get it delivered at my
                  doorstep."
                </p>
              </div>
            </div>
          </ScrollReveal>

          {/* Key Insights Derived from User Persona */}
          <ScrollReveal delay={0.5}>
            <div>
              <div className="flex items-center gap-3 mb-8">
                <Lightbulb className="w-8 h-8 text-[#fd5b38]" />
                <h3 className="text-3xl">
                  Key Insights Derived from User Persona
                </h3>
              </div>

              <div className="space-y-6">
                {[
                  "Users would like to have fresh groceries every morning.",
                  "Users want to get the groceries delivered quickly, as they might have started cooking, but a few things might be missing to complete that dish.",
                  "Users are always looking for offers and discounts so that they can save money, which they usually don't get at local kirana stores.",
                  "Users are already too busy in their lives, and ordering groceries will definitely save some of their time. Considering the hypothetical users, we have come up with a few features, like providing them with great discounts and providing them with an option for quick delivery within 10-15 minutes so that they can get their groceries even before they complete their cooking. Users will definitely save some time by ordering groceries that will be delivered to their doorstep.",
                ].map((insight, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: i * 0.08,
                      type: "spring",
                      stiffness: 100,
                    }}
                    className="group"
                  >
                    <div className="p-6 rounded-xl bg-gradient-to-r from-[#fd5b38]/10 via-[#fd5b38]/5 to-transparent border-l-4 border-[#fd5b38] hover:from-[#fd5b38]/15 hover:via-[#fd5b38]/8 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#fd5b38] mt-2.5" />
                        <p className="text-gray-300 text-lg group-hover:text-white transition-colors">
                          {insight}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* Empathy Map */}
      <section className="py-32 px-6 bg-gradient-to-b from-transparent via-[#fd5b38]/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Empathy Map</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="mb-12">
              <p className="text-xl text-gray-300">
                Empathizing helps us recognize the users and gain a perspective
                similar to theirs. The empathy map will also allow us to
                prioritize feature requests. We have understood the way they
                live and how they will think, feel, say, and do. All these
                points are put down together in an empathy map.
              </p>
            </div>
          </ScrollReveal>

          {/* Empathy Map Grid */}
          <ScrollReveal delay={0.3}>
            <div className="relative mb-16">
              {/* Main Grid Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* THINK & FEEL - Top Left */}
                <div className="lg:col-span-1 space-y-4">
                  <div className="p-6 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 item-border-2 border-purple-500/30">
                    <h3 className="text-2xl mb-6 text-purple-400 flex items-center gap-2">
                      <Heart className="w-6 h-6" />
                      THINK & FEEL
                    </h3>
                    <div className="space-y-3">
                      {[
                        "Will I be able to order groceries quickly?",
                        "Worried about which App to use",
                        "Worried about groceries not being correct or fresh",
                        "Will I be able to get fresh groceries everyday?",
                        "Will customer care people Will reply to my queries?",
                        "Will I be able to modify my subscriptions?",
                        "What if I am not able to track my order?",
                      ].map((item, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.05 }}
                          className="p-3 bg-yellow-400/90 text-gray-900 rounded shadow-md transform rotate-[-1deg] hover:rotate-0 transition-transform cursor-pointer text-sm"
                        >
                          {item}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Center Column - SEE and Persona */}
                <div className="lg:col-span-1 space-y-6">
                  {/* SEE - Top */}
                  <div className="p-6 rounded-xl bg-gradient-to-br from-green-500/20 to-green-500/5 item-border-2 border-green-500/30">
                    <h3 className="text-2xl mb-6 text-green-400 flex items-center gap-2">
                      <Star className="w-6 h-6" />
                      SEE
                    </h3>
                    <div className="space-y-3">
                      {[
                        "Should I also post videos to increase the outreach?",
                        "My story is not that compelling",
                        "Other people have raised money but I am not able to",
                        "What other options can I use on social media to reach everyone?",
                        "Worried if people stop donating after few days",
                        "Where can I find examples for posts to share it?",
                      ].map((item, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.05 }}
                          className="p-3 bg-pink-400/90 text-gray-900 rounded shadow-md transform rotate-[1deg] hover:rotate-0 transition-transform cursor-pointer text-sm"
                        >
                          {item}
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Persona - Center */}
                  <div className="p-8 rounded-xl bg-gradient-to-br from-[#fd5b38]/20 to-[#fd5b38]/5 item-border-2 border-[#fd5b38]/30 text-center">
                    <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#fd5b38] to-orange-600 mb-4 flex items-center justify-center text-4xl">
                      👨‍💻
                    </div>
                    <h3 className="text-3xl text-[#fd5b38]">Animesh</h3>
                    <p className="text-gray-400 mt-2">IT Employee, 23</p>
                  </div>
                </div>

                {/* HEAR - Top Right */}
                <div className="lg:col-span-1 space-y-4">
                  <div className="p-6 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 item-border-2 border-blue-500/30">
                    <h3 className="text-2xl mb-6 text-blue-400 flex items-center gap-2">
                      <MessageSquare className="w-6 h-6" />
                      HEAR
                    </h3>
                    <div className="space-y-3">
                      {[
                        "There is no way you can get your delivery within 10 minutes",
                        "Try going to Grocery stores for fresh groceries",
                        "You need to read reviews before buying it. You cannot trust these apps",
                        "It's difficult to trust the app and their services",
                        "No one would Order without seeing their quality",
                        "You will be spammed with notifications",
                      ].map((item, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.05 }}
                          className="p-3 bg-blue-400/90 text-gray-900 rounded shadow-md transform rotate-[2deg] hover:rotate-0 transition-transform cursor-pointer text-sm"
                        >
                          {item}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* SAY & DO - Bottom Left */}
                <div className="lg:col-span-1 space-y-4">
                  <div className="p-6 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-500/5 item-border-2 border-orange-500/30">
                    <h3 className="text-2xl mb-6 text-orange-400 flex items-center gap-2">
                      <MessageSquare className="w-6 h-6" />
                      SAY & DO
                    </h3>
                    <div className="space-y-3">
                      {[
                        "Asks people to drop milk and vegetables in the morning",
                        "Worried about not able get order on time",
                        "How frequently should I order to have fresh groceries?",
                        "Asks for help to do grocery shopping from stores",
                        "Worried about not able to track the order and delivery person",
                      ].map((item, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.05 }}
                          className="p-3 bg-orange-400/90 text-gray-900 rounded shadow-md transform rotate-[-2deg] hover:rotate-0 transition-transform cursor-pointer text-sm"
                        >
                          {item}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* GAINS - Bottom Center */}
                <div className="lg:col-span-1 space-y-4">
                  <div className="p-6 rounded-xl bg-gradient-to-br from-green-500/20 to-green-500/5 item-border-2 border-green-500/30">
                    <h3 className="text-2xl mb-6 text-green-400 flex items-center gap-2">
                      <TrendingUp className="w-6 h-6" />
                      GAINS
                    </h3>
                    <div className="space-y-3">
                      {[
                        "Easily order the groceries from different categories",
                        "Easily able to get the fresh groceries every morning",
                        "Cheaper prices than local supermarket, great discounts and cash backs",
                        "Super-fast delivery for your valuable groceries",
                      ].map((item, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.05 }}
                          className="p-3 bg-green-400/90 text-gray-900 rounded shadow-md transform rotate-[1deg] hover:rotate-0 transition-transform cursor-pointer text-sm"
                        >
                          {item}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* PAINS - Bottom Right */}
                <div className="lg:col-span-1 space-y-4">
                  <div className="p-6 rounded-xl bg-gradient-to-br from-red-500/20 to-red-500/5 item-border-2 border-red-500/30">
                    <h3 className="text-2xl mb-6 text-red-400 flex items-center gap-2">
                      <AlertCircle className="w-6 h-6" />
                      PAINS
                    </h3>
                    <div className="space-y-3">
                      {[
                        "Not sure how much time it will take to deliver",
                        "Not sure at what time I'll be getting fresh groceries in morning",
                        "Not aware of all the brands available in the market",
                        "Not aware about how customer support will reply",
                      ].map((item, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.05 }}
                          className="p-3 bg-red-400/90 text-gray-900 rounded shadow-md transform rotate-[-1deg] hover:rotate-0 transition-transform cursor-pointer text-sm"
                        >
                          {item}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Key Insights Derived from Empathy Map */}
          <ScrollReveal delay={0.5}>
            <div>
              <div className="flex items-center gap-3 mb-8">
                <Lightbulb className="w-8 h-8 text-[#fd5b38]" />
                <h3 className="text-3xl">
                  Key Insights Derived from Empathy Map
                </h3>
              </div>

              <div className="space-y-6">
                {[
                  "Users will easily search the items and can order from different brands and categories",
                  "Easy returns and refunds are possible if the quality needs to be better.",
                  "Users don't have to find people who can provide fresh milk in the morning Considering the hypothetical users for empathy maps, we have come up with features like having a search bar in the categories section, providing more discounts than local kirana stores, and giving the option of real-time order tracking.",
                ].map((insight, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: i * 0.08,
                      type: "spring",
                      stiffness: 100,
                    }}
                    className="group"
                  >
                    <div className="p-6 rounded-xl bg-gradient-to-r from-[#fd5b38]/10 via-[#fd5b38]/5 to-transparent border-l-4 border-[#fd5b38] hover:from-[#fd5b38]/15 hover:via-[#fd5b38]/8 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[#fd5b38] mt-2.5" />
                        <p className="text-gray-300 text-lg group-hover:text-white transition-colors">
                          {insight}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* Information Architecture */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Information Architecture</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="mb-12">
              <p className="text-xl text-gray-300">
                The following structural design of shared information aims at
                organizing content and all the features so that users would
                adjust to the functionality of the product and will be able to
                find everything they need without any effort.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.4}>
            <div className="p-8 md:p-12 rounded-2xl bg-gradient-to-br from-[#fd5b38]/10 via-white/5 to-white/0 item-border border-[#fd5b38]/20">
              <div className="flex items-center justify-center">
                <div className="text-center space-y-6">
                  <div>
                    <motion.img
                      src="/assets/imgs/case-studies/green_basket_1.png"
                      alt="Information Architecture Diagram"
                      className="w-full h-auto rounded-xl shadow-2xl"
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  </div>
                  <div className="grid md:grid-cols-3 gap-6 pt-8">
                    <motion.div
                      className="p-6 rounded-xl bg-gradient-to-br from-white/5 to-white/0 item-border border-white/10 hover:border-[#fd5b38]/50 transition-all"
                      whileHover={{ scale: 1.05, y: -5 }}
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#fd5b38]/20 flex items-center justify-center mb-4 mx-auto">
                        <Layers className="w-6 h-6 text-[#fd5b38]" />
                      </div>
                      <h4 className="text-lg mb-2">Clear Hierarchy</h4>
                      <p className="text-sm text-gray-400">
                        Logical organization of features and content
                      </p>
                    </motion.div>

                    <motion.div
                      className="p-6 rounded-xl bg-gradient-to-br from-white/5 to-white/0 item-border border-white/10 hover:border-[#fd5b38]/50 transition-all"
                      whileHover={{ scale: 1.05, y: -5 }}
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#fd5b38]/20 flex items-center justify-center mb-4 mx-auto">
                        <Target className="w-6 h-6 text-[#fd5b38]" />
                      </div>
                      <h4 className="text-lg mb-2">Easy Navigation</h4>
                      <p className="text-sm text-gray-400">
                        Intuitive paths to user goals
                      </p>
                    </motion.div>

                    <motion.div
                      className="p-6 rounded-xl bg-gradient-to-br from-white/5 to-white/0 item-border border-white/10 hover:border-[#fd5b38]/50 transition-all"
                      whileHover={{ scale: 1.05, y: -5 }}
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#fd5b38]/20 flex items-center justify-center mb-4 mx-auto">
                        <CheckCircle className="w-6 h-6 text-[#fd5b38]" />
                      </div>
                      <h4 className="text-lg mb-2">User-Centered</h4>
                      <p className="text-sm text-gray-400">
                        Designed around user needs
                      </p>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* Key Features */}
      <section className="py-32 px-6 bg-gradient-to-b from-transparent via-[#fd5b38]/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Key Features</h2>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Zap,
                title: "Quick Delivery",
                description:
                  "Get groceries delivered in 10-15 minutes for urgent needs",
                color: "from-yellow-500 to-orange-500",
              },
              {
                icon: ShoppingCart,
                title: "Smart Subscriptions",
                description:
                  "Subscribe to products with daily, weekly, or monthly frequency",
                color: "from-[#fd5b38] to-red-500",
              },
              {
                icon: Truck,
                title: "Real-time Tracking",
                description:
                  "Track your order in real-time from store to doorstep",
                color: "from-blue-500 to-cyan-500",
              },
              {
                icon: Star,
                title: "Best Deals",
                description:
                  "Access exclusive offers and bank deals unavailable at local stores",
                color: "from-purple-500 to-pink-500",
              },
            ].map((feature, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div
                  className="p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/0 item-border border-white/10 hover:border-[#fd5b38]/50 transition-all group cursor-pointer"
                  whileHover={{ scale: 1.05, y: -10 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                  >
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl mb-3">{feature.title}</h3>
                  <p className="text-gray-400">{feature.description}</p>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={0.8}>
            <div className="mt-16 flex justify-center gap-8 text-gray-400">
              <div className="text-center">
                <div className="text-3xl text-[#fd5b38] mb-2">10</div>
                <div className="text-sm">Users Interviewed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl text-[#fd5b38] mb-2">5</div>
                <div className="text-sm">Competitors Analyzed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl text-[#fd5b38] mb-2">10min</div>
                <div className="text-sm">Delivery Time</div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Wireframes */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Wireframes</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="mb-12">
              <p className="text-xl text-gray-300">
                After finalizing the user flow, I created wireframes for all
                screens that helped me understand the overall product structure
                and how the user will experience different flows and granular
                requirements of fundamental UI elements. Wireframes also helped
                me symbolize my process of thought on paper.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.4}>
            <img
              src="/assets/imgs/case-studies/green_basket_2.png"
              alt="Wireframes"
              className="w-full h-auto"
            />
          </ScrollReveal>
        </div>
      </section>
      {/* Branding & Colors */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Branding & Color Palette</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="mb-12">
              <h3 className="text-2xl mb-4">Branding</h3>
              <p className="text-xl text-gray-300">
                Creating and maintaining a consistent UI and UX across every
                part of the app helps users intuitively navigate and
                successfully interact with different sections of the application
                without confusion. Consistent branding will also help to ensure
                the design is communicating the deliberate message
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.15}>
            <div className="mb-12">
              <h3 className="text-2xl mb-6">Color Palette</h3>
              <div className="space-y-4 text-gray-300">
                <p className="flex items-start gap-3">
                  <span className="text-[#fd5b38] flex-shrink-0 mt-1">●</span>
                  <span>
                    The primary green color is associated with health,
                    tranquility, power, and nature. Also, green is a symbol of
                    freshness. The color symbolizes the places that sell fresh
                    herbs and healthy ingredients of different kinds. Green also
                    reminds users of fields and farming.
                  </span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-[#fd5b38] flex-shrink-0 mt-1">●</span>
                  <span>
                    The secondary orange color is associated with portraying
                    value and affordability.
                  </span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="text-[#fd5b38] flex-shrink-0 mt-1">●</span>
                  <span>
                    The white background makes the application clean and
                    minimalistic. It also helps to create a contrast between
                    bright colors and font to make it easy to focus on the
                    important parts. It also offers a sense of peace and calm
                    while creating a sense of order and efficiency. It will
                    allow users to quickly scan and read the content and help
                    avoid clutter on the UI.
                  </span>
                </p>
              </div>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <ScrollReveal delay={0.2}>
              <div className="space-y-4">
                <div className="h-48 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-white text-xl mb-2">Primary Green</div>
                    <div className="text-white/80">#22C55E</div>
                  </div>
                </div>
                <p className="text-gray-400 text-sm">
                  Associated with health, freshness, and nature. Symbolizes
                  fresh herbs and farming.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <div className="space-y-4">
                <div className="h-48 rounded-2xl bg-gradient-to-br from-[#fd5b38] to-orange-600 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-white text-xl mb-2">
                      Secondary Orange
                    </div>
                    <div className="text-white/80">#FD5B38</div>
                  </div>
                </div>
                <p className="text-gray-400 text-sm">
                  Portrays value and affordability. Creates energy and warmth in
                  the interface.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <div className="space-y-4">
                <div className="h-48 rounded-2xl bg-white flex items-center justify-center item-border border-gray-300">
                  <div className="text-center">
                    <div className="text-gray-900 text-xl mb-2">
                      Clean White
                    </div>
                    <div className="text-gray-600">#FFFFFF</div>
                  </div>
                </div>
                <p className="text-gray-400 text-sm">
                  Creates a clean, minimalistic feel. Ensures readability and
                  reduces clutter.
                </p>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.6}>
            <div className="p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/0 item-border border-white/10">
              <h3 className="text-2xl mb-4">Typography</h3>
              <div className="space-y-4">
                <div>
                  <div
                    className="text-4xl mb-2"
                    style={{ fontFamily: "Poppins, sans-serif" }}
                  >
                    Poppins
                  </div>
                  <p className="text-gray-400">
                    Poppins has a great range of font styles and font weights.
                    Also, it is comfortable to read and gives a lively feel to
                    the application.
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* Login/Signup Screens */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Login/Signup Screens</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="mb-12">
              <p className="text-xl text-gray-300">
                Users can use their mobile numbers or social media accounts to
                log in/register. While some of the new users want to explore the
                app and compare their prices against competitors' apps.
                Empathizing with users as they vary in giving personal details
                before experiencing a platform, which in turn builds trust.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.4}>
            <div className="relative rounded-2xl overflow-hidden item-border-2 border-[#fd5b38]/20 shadow-2xl">
              <img
                src="/assets/imgs/case-studies/green_basket_3.png"
                alt="Login/Signup Screen UI"
                className="w-full h-auto"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white text-lg">Login/Signup Screens</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* Home Screen */}
      <section className="py-32 px-6 bg-gradient-to-b from-transparent via-[#fd5b38]/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Home Screen</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="mb-12">
              <p className="text-xl text-gray-300">
                Users can get an option to repurchase from their last orders.
                Users can also shop by categories or best deals. They can also
                see all the new offers, bank deals, and new products on the app.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.4}>
            <div className="flex justify-center relative rounded-2xl overflow-hidden item-border-2 border-[#fd5b38]/20 shadow-2xl">
              <img
                src="/assets/imgs/case-studies/green_basket_4.png"
                alt="Home Screen UI"
                className="h-auto"
                style={{ width: "50%" }}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white text-lg">Home Screen UI</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* Ordering Flow */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Ordering Flow</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="mb-12">
              <p className="text-xl text-gray-300">
                Users will be able to select the category and subcategory. The
                user will be able to search for products in the categories
                section, too. Once the user selects a particular product, they
                will see all the details. They can subscribe to the product or
                buy it once. Once they add all the products to the cart, they
                get to select the delivery option and place the order. Users
                will be able to do real-time tracking of the order.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.4}>
            <div className="relative rounded-2xl overflow-hidden item-border-2 border-[#fd5b38]/20 shadow-2xl">
              <img
                src="/assets/imgs/case-studies/green_basket_5.png"
                alt="Ordering Flow UI"
                className="w-full h-auto"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white text-lg">Ordering Flow UI</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* Subscription Flow */}
      <section className="py-32 px-6 bg-gradient-to-b from-transparent via-[#fd5b38]/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Subscription flow</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="mb-12">
              <p className="text-xl text-gray-300">
                When users select to subscribe to the product, they can
                subscribe daily, on alternate days, weekly, or monthly. Once any
                option is selected, they can select the start date. The users
                will receive the products at 7 AM at their doorstep.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.4}>
            <div className="relative rounded-2xl overflow-hidden item-border-2 border-[#fd5b38]/20 shadow-2xl">
              <img
                src="/assets/imgs/case-studies/green_basket_6.png"
                alt="Subscription Flow UI"
                className="w-full h-auto"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white text-lg">Subscription Flow UI</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      {/* Usability Testing */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Usability Testing</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="mb-12">
              <p className="text-xl text-gray-300">
                The prototype was tested with ten users who usually order
                groceries from the online app. Eventually, we had an all-in-one
                on-demand grocery delivery platform ready that covered the
                solutions to all their problems, pain points, and expectations.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              "Users can subscribe or buy it once from one app only.",
              "They would be able to find the best discounts and offers.",
              "Their order gets delivered within 10 minutes.",
              "They would get fresh groceries early in the morning.",
              "They would be able to get groceries hassle-free.",
              "They would be able to do real-time tracking of their placed orders.",
            ].map((result, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div
                  className="p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/0 item-border border-white/10 hover:border-[#fd5b38]/50 transition-all"
                  whileHover={{ x: 10 }}
                >
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-[#fd5b38] flex-shrink-0" />
                    <span className="text-gray-300">{result}</span>
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      {/* Conclusion */}
      <section className="py-12 px-6 bg-gradient-to-b from-transparent via-[#fd5b38]/10 to-[#fd5b38]/5">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="flex items-center gap-3 mb-16">
              <div className="w-12 h-1 bg-[#fd5b38]" />
              <h2 className="text-4xl md:text-5xl">Conclusion</h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="mb-12">
              <p className="text-xl text-gray-300">
                Working on a project with a time constraint was a challenge for
                me. The right amount of research helped to make an app that
                would impact the users' daily lives. The problem that got solved
                via this application is a part of people's lives today in one
                way or the other. I thoroughly enjoyed the research and
                interview process, all the way up to mocking high-fidelity
                visual identities. A project is never finished, but working on
                collecting more feedback, exploring use cases, and brainstorming
                ideas for any other features that get highlighted from the
                feedback.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>
      <section className="py-12 px-6 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto text-center text-gray-400">
          <p>Green Basket — UX Case Study 2025</p>
          <p className="text-sm mt-2">
            Designed with passion for better user experiences
          </p>
        </div>
      </section>
    </div>
  );
}
